import { Loader } from '@sd/components';
import {
  accountGroupIdState,
  APP_CONFIG,
  authenticatedUserState,
  authVerifiedState,
  impersonatedUserState,
  ROUTES,
  sdLegacySessionService,
  sellerNumberOverridesState,
  sessionIdState,
  useAsyncEffect,
  useSetImpersonatedUserState
} from '@sd/shared';
import { ReactNode, useState } from 'react';
import { useHistory } from 'react-router';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getLoginUrl } from '../utils/getLoginUrl';
import { logout } from '../utils/logout';

type Props = {
  children: ReactNode;
};

/**
 * Guards children from being rendered until the user's login is verified to be
 * valid and up-to-date
 */
export const Authenticated = ({ children }: Props) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const sessionId = useRecoilValue(sessionIdState);
  const history = useHistory();
  const authVerified = useRecoilValue(authVerifiedState);
  const authenticatedUser = useRecoilValue(authenticatedUserState);
  const impersonatedUser = useRecoilValue(impersonatedUserState);
  const setImpersonatedUser = useSetImpersonatedUserState();
  const setSellerNumberOverrides = useSetRecoilState(sellerNumberOverridesState);
  const setAccountGroupId = useSetRecoilState(accountGroupIdState);

  useAsyncEffect(async () => {
    if (authenticatedUser && sessionId && authVerified) {
      const { session_user: legacySession, account_group_id: accountGroupId } =
        await sdLegacySessionService.getOrCreateSession(sessionId);
      const usersDontMatch =
        authenticatedUser.username !== (legacySession.impersonator?.username || legacySession.username);
      if (usersDontMatch) {
        // Full stop!!
        return await logout();
      }

      if (authenticatedUser.employee) {
        if (legacySession.impersonating_by_seller_number) {
          // impersonating by seller numbers or account group
          await setImpersonatedUser(undefined);
          setAccountGroupId(accountGroupId);
          setSellerNumberOverrides(legacySession.seller_numbers);
        } else if (legacySession.impersonating) {
          // Legacy session is impersonating
          if (impersonatedUser?.username !== legacySession.username) {
            await setImpersonatedUser(legacySession.username);
          }
        } else {
          await setImpersonatedUser(undefined);
          const userIsOnAnAdminPageAlready = history.location.pathname.startsWith(
            ROUTES.admin.accountSelect.locationFor({}).pathname!
          );
          if (!userIsOnAnAdminPageAlready) {
            if (['prod', 'uat'].includes(APP_CONFIG.build.environment)) {
              window.location.assign(ROUTES.external.legacySellerDashboard.admin.accountSelect.externalUrlFor());
            } else {
              history.push(ROUTES.admin.accountSelect.locationFor({ backUrl: history.location.pathname }));
            }
          }
        }
      }

      setLoggedIn(true);
    } else {
      location.assign(getLoginUrl());
    }
  }, []);

  return <Loader loading={!loggedIn}>{loggedIn && children}</Loader>;
};
